import { t } from "../i18n/index.ts";
import { navIcon } from "./review.ts";

/** Shared, local-only playback of recorded positions. Never submits a move. */
export class MoveReplay {
  private timer?: number;
  private button: HTMLButtonElement;
  private board: HTMLElement | null;
  private onClick = () => this.toggle();
  private onBoardInput = () => this.pause();
  private onVisibility = () => { if (document.hidden) this.pause(); };

  constructor(
    root: HTMLElement,
    private position: () => { current: number; last: number; blocked?: boolean },
    private show: (ply: number) => void,
  ) {
    this.button = root.querySelector<HTMLButtonElement>("[data-replay]")!;
    this.board = root.querySelector<HTMLElement>(".cg-board-wrap");
    this.button.addEventListener("click", this.onClick);
    this.board?.addEventListener("pointerdown", this.onBoardInput);
    document.addEventListener("visibilitychange", this.onVisibility);
    this.update();
  }

  private toggle(): void {
    if (this.timer !== undefined) { this.pause(); return; }
    const { current, last, blocked } = this.position();
    if (blocked || last <= 0) return;
    // Boards open on their final position. Replay from the beginning there;
    // an earlier selection (or a paused replay) continues where it is shown.
    if (current >= last) this.show(0);
    this.timer = window.setInterval(() => {
      const { current, last, blocked } = this.position();
      if (blocked || !this.button.isConnected) { this.pause(); return; }
      if (current < last) this.show(current + 1);
      this.update();
    }, 1000);
    this.update();
  }

  pause(): void {
    if (this.timer !== undefined) window.clearInterval(this.timer);
    this.timer = undefined;
    this.paint();
  }

  /** Called after navigation, history changes, or pending-command changes. */
  update(): void {
    const { current, last, blocked } = this.position();
    if (blocked || current >= last || last <= 0) this.pause();
    this.button.disabled = blocked === true || last <= 0;
    this.paint();
  }

  private paint(): void {
    const playing = this.timer !== undefined;
    const label = t(playing ? "review.pause" : "review.play");
    this.button.setAttribute("aria-pressed", String(playing));
    this.button.setAttribute("aria-label", label);
    this.button.title = label;
    this.button.innerHTML = `${navIcon(playing ? "pause" : "play")}<span>${label}</span>`;
  }

  destroy(): void {
    this.pause();
    this.button.removeEventListener("click", this.onClick);
    this.board?.removeEventListener("pointerdown", this.onBoardInput);
    document.removeEventListener("visibilitychange", this.onVisibility);
  }
}
